import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  ChevronLeft,
  Building2,
  Copy,
  Loader2,
  Trash2,
  Save,
  AlertTriangle,
  Target,
  FileSignature,
  Sparkles,
  Send,
  CalendarClock,
  TrendingUp,
  ClipboardList,
  CalendarDays,
  Receipt,
  FileText,
  MessageSquare,
  History,
  type LucideIcon,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { SELECTOR_PAGE_SIZE } from '../lib/constants'
import { formatCents } from '../lib/money'
import ProposalGeneratorModal from '../components/ai/ProposalGeneratorModal'
import EmailComposer from '../components/comms/EmailComposer'
import SentEmailsPanel from '../components/comms/SentEmailsPanel'
import { currentLocaleTag } from '../lib/formatting'
import {
  fractionToPctInput,
  isOverCommissionCap,
  normalizeAdvisorList,
  pctInputToFraction,
} from '../lib/advisors'
import AssignmentsPanel from '../components/cases/AssignmentsPanel'
import DocumentsPanel from '../components/cases/DocumentsPanel'
import TimelinePanel from '../components/cases/TimelinePanel'
import CaseCalendarPanel from '../components/cases/CaseCalendarPanel'
import ExecutionPanel from '../components/cases/ExecutionPanel'
import CaseAiPanel from '../components/cases/CaseAiPanel'
import BillingPlanWizard from '../components/cases/BillingPlanWizard'
import FundsProvisionPanel from '../components/billing/FundsProvisionPanel'
import MilestonesPanel from '../components/billing/MilestonesPanel'
import OpportunityRecurringPanel from '../components/billing/OpportunityRecurringPanel'
import RenewalAlert from '../components/billing/RenewalAlert'
import CaseTasksPanel from '../components/cases/CaseTasksPanel'
import CaseTimePanel from '../components/cases/CaseTimePanel'
import CaseExpensesPanel from '../components/cases/CaseExpensesPanel'
import {
  PipelineStageLabel,
  type AdvisorDto,
  type OpportunityDetail,
  type PipelineStage,
  type UpdateOpportunityInput,
} from '@jr2os/shared'
import { useServiceLines } from '../lib/service-lines'
import BillingModeBadge from '../components/cases/BillingModeBadge'
import ServiceLineEditor, {
  draftsToServiceLinesInput,
  serviceLinesToDrafts,
  type ServiceLineDraft,
} from '../components/cases/ServiceLineEditor'
import SuggestServiceLinesModal from '../components/cases/SuggestServiceLinesModal'

// M5 — Ficha organizada en pestañas (mismo patrón que ClientDetailPage).
type CaseTabId =
  | 'resumen'
  | 'ejecucion'
  | 'actividad'
  | 'facturacion'
  | 'documentos'
  | 'comunicaciones'
  | 'cronologia'

const CASE_TABS: { id: CaseTabId; labelKey: string; icon: LucideIcon }[] = [
  { id: 'resumen', labelKey: 'tabs.resumen', icon: TrendingUp },
  { id: 'ejecucion', labelKey: 'tabs.ejecucion', icon: ClipboardList },
  { id: 'actividad', labelKey: 'tabs.actividad', icon: CalendarDays },
  { id: 'facturacion', labelKey: 'tabs.facturacion', icon: Receipt },
  { id: 'documentos', labelKey: 'tabs.documentos', icon: FileText },
  { id: 'comunicaciones', labelKey: 'tabs.comunicaciones', icon: MessageSquare },
  { id: 'cronologia', labelKey: 'tabs.cronologia', icon: History },
]

export default function CaseDetailPage() {
  const { t } = useTranslation(['opportunities', 'enums'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { lines: catalogLines, nameOf } = useServiceLines()
  const [data, setData] = useState<OpportunityDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState<UpdateOpportunityInput>({ additionalServiceLines: [] })
  const [edited, setEdited] = useState(false)
  const [advisors, setAdvisors] = useState<AdvisorDto[]>([])
  const [advisorId, setAdvisorId] = useState('')
  // Comisión del asesor mostrada como % editable; se envía como fracción.
  const [advisorCommissionPct, setAdvisorCommissionPct] = useState('')
  // LOTE B — líneas de servicio de la oportunidad (importe + facturación +
  // recurrencia por línea).
  const [drafts, setDrafts] = useState<ServiceLineDraft[]>([])
  const [showProposalModal, setShowProposalModal] = useState(false)
  // Sugerir líneas de facturación de la propuesta con IA (solo mientras es
  // oportunidad, no proyecto). Añade las partidas facturables al editor.
  const [showSuggestLines, setShowSuggestLines] = useState(false)
  // F29 — Email composer: lo abrimos al pulsar "Enviar email". Cargamos el
  // email del cliente desde /clients/:id (OpportunityDetail no lo incluye).
  const [composerOpen, setComposerOpen] = useState(false)
  // F42 — Wizard del plan de facturación (solo oportunidades GANADAS).
  const [planWizardOpen, setPlanWizardOpen] = useState(false)
  const [clientEmail, setClientEmail] = useState<string | null>(null)
  const [sentEmailsRefreshKey, setSentEmailsRefreshKey] = useState(0)
  // M5 — pestaña activa de la ficha.
  const [activeTab, setActiveTab] = useState<CaseTabId>('resumen')

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { opportunity: c } = await api.get<{ opportunity: OpportunityDetail }>(
        `/opportunities/${id}`
      )
      setData(c)
      setEditForm({
        title: c.title,
        clientId: c.clientId,
        serviceLine: c.serviceLine,
        stage: c.stage,
        description: c.description,
        lostReason: c.lostReason,
        administrativeClose: c.administrativeClose,
        additionalServiceLines: c.additionalServiceLines ?? [],
      })
      setAdvisorId(c.advisorId ?? '')
      setAdvisorCommissionPct(fractionToPctInput(c.advisorCommissionPct))
      // LOTE B — drafts desde el detalle por línea del backend (importe,
      // facturación y recurrencia por línea).
      setDrafts(serviceLinesToDrafts(c.serviceLines ?? []))
      setEdited(false)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(t('detail.errors.notFound'))
      } else {
        setError(err instanceof Error ? err.message : t('detail.errors.loadFailed'))
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void reload()
  }, [reload])

  // Rentabilidad al día: los tiempos/gastos de Actividad (y la facturación)
  // cambian CAC/margen, que se calculan en el backend. Al VOLVER a Resumen
  // refrescamos solo `data` en silencio — sin tocar editForm/drafts, que
  // pueden tener cambios sin guardar, y sin spinner de página.
  const prevTabRef = useRef<CaseTabId>('resumen')
  useEffect(() => {
    const cameFrom = prevTabRef.current
    prevTabRef.current = activeTab
    if (activeTab !== 'resumen' || cameFrom === 'resumen' || !id) return
    let cancelled = false
    void (async () => {
      try {
        const { opportunity: c } = await api.get<{ opportunity: OpportunityDetail }>(
          `/opportunities/${id}`,
        )
        if (!cancelled) setData(c)
      } catch {
        /* silencioso: el Resumen se queda con el dato anterior */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeTab, id])

  useEffect(() => {
    void (async () => {
      try {
        const resp = await api.get<unknown>('/advisors?active=true')
        setAdvisors(normalizeAdvisorList(resp))
      } catch {
        setAdvisors([])
      }
    })()
  }, [])

  // Clientes para el selector de la ficha (reasignar tras duplicar). Carga
  // única, alfabética; si falla, el selector muestra solo el cliente actual.
  const [clientOptions, setClientOptions] = useState<{ id: string; displayName: string }[]>([])
  useEffect(() => {
    void (async () => {
      try {
        const resp = await api.get<{ items: { id: string; displayName: string }[] }>(
          `/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`,
        )
        setClientOptions(resp.items.map((c) => ({ id: c.id, displayName: c.displayName })))
      } catch {
        setClientOptions([])
      }
    })()
  }, [])

  // F29 — Cargamos el email del cliente cuando se conoce el clientId.
  useEffect(() => {
    if (!data?.clientId) {
      setClientEmail(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const { client } = await api.get<{ client: { email: string | null } }>(
          `/clients/${data.clientId}`,
        )
        if (!cancelled) setClientEmail(client.email ?? null)
      } catch {
        if (!cancelled) setClientEmail(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [data?.clientId])

  const upd = <K extends keyof UpdateOpportunityInput>(k: K, v: UpdateOpportunityInput[K]) => {
    setEditForm((f) => ({ ...f, [k]: v }))
    setEdited(true)
  }

  const overCap = isOverCommissionCap(advisorCommissionPct)

  const onPickAdvisor = (newId: string) => {
    setAdvisorId(newId)
    // Al elegir asesor, prefijamos su comisión por defecto (fracción → %).
    if (newId) {
      const a = advisors.find((x) => x.id === newId)
      setAdvisorCommissionPct(a ? fractionToPctInput(a.defaultCommissionPct) : '')
    } else {
      setAdvisorCommissionPct('')
    }
    setEdited(true)
  }

  const onSave = async () => {
    if (!id) return
    if (advisorId && overCap) {
      toast.error(t('detail.alerts.commissionOverCap'))
      return
    }
    if (drafts.length === 0) {
      toast.error(t('detail.alerts.pickAtLeastOneLine'))
      return
    }
    // F42 — Capturamos el estado ANTES del guardado/reload (evita depender del
    // closure): si ya estaba GANADA, si pasa a GANADA, y si hay línea facturable.
    const wasWon = data?.stage === 'GANADA' || data?.stage === 'EN_INVERSION'
    const willBeWon = editForm.stage === 'GANADA' || editForm.stage === 'EN_INVERSION'
    const hasFacturable = drafts.some((d) => d.billingMode === 'FACTURABLE')
    setSaving(true)
    try {
      // editForm puede traer serviceLine / additionalServiceLines / billingMode
      // legacy; los quitamos: el backend deriva todo del detalle por línea.
      const editPayload: Record<string, unknown> = { ...editForm }
      delete editPayload.serviceLine
      delete editPayload.additionalServiceLines
      delete editPayload.estimatedValueCents
      delete editPayload.billingMode
      await api.patch(`/opportunities/${id}`, {
        ...editPayload,
        // Comisión visible como % → enviada como fracción (5 → 0.05).
        advisorId: advisorId || null,
        advisorCommissionPct: advisorId ? pctInputToFraction(advisorCommissionPct) : null,
        // LOTE B — detalle por línea (fuente de verdad; el backend deriva el resto).
        serviceLines: draftsToServiceLinesInput(drafts),
      })
      await reload()
      // F42 — Auto-prompt: si pasa a GANADA y hay alguna línea facturable,
      // ofrecemos configurar el plan de facturación (el backend lo rechaza con
      // 409 si la oportunidad no tiene ninguna línea facturable).
      if (!wasWon && willBeWon && hasFacturable) {
        if (await confirmDialog(t('billingPlan.wonPrompt'))) setPlanWizardOpen(true)
      }
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      toast.error(body?.error ?? (err instanceof Error ? err.message : t('detail.alerts.genericError')))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!data) return
    if (
      !(await confirmDialog({ message: t('detail.confirm.delete', { code: data.code }), tone: 'danger' }))
    )
      return
    try {
      await api.delete(`/opportunities/${data.id}`)
      navigate('/opportunities')
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      toast.error(body?.error ?? (err instanceof Error ? err.message : t('detail.alerts.genericError')))
    }
  }

  // Duplicar: crea una oferta nueva (LEAD) con TODOS los datos y líneas de esta,
  // editable. Navega a la copia recién creada.
  const [duplicating, setDuplicating] = useState(false)
  const onDuplicate = async () => {
    if (!data || duplicating) return
    setDuplicating(true)
    try {
      const { opportunity } = await api.post<{ opportunity: OpportunityDetail }>(
        `/opportunities/${data.id}/duplicate`,
      )
      navigate(`/opportunities/${opportunity.id}`)
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      toast.error(body?.error ?? (err instanceof Error ? err.message : t('detail.alerts.genericError')))
    } finally {
      setDuplicating(false)
    }
  }

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> {t('detail.loading')}
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="max-w-xl mx-auto pt-12 surface p-8 text-center">
        <AlertTriangle size={32} className="mx-auto text-amber-700 dark:text-amber-300 mb-3" />
        <h2 className="text-xl font-serif font-semibold t-primary mb-1">{t('detail.unavailable.title')}</h2>
        <p className="t-muted text-sm mb-4">{error ?? t('detail.unavailable.fallback')}</p>
        <Link
          to="/opportunities"
          className="inline-flex items-center gap-1.5 text-sm text-jr2-700 dark:text-jr2-300 hover:underline"
        >
          <ChevronLeft size={14} /> {t('detail.backToList')}
        </Link>
      </div>
    )
  }

  const disabled = !data.canWrite
  // Proyecto = oportunidad ganada (o en inversión). Mientras NO lo sea, la IA
  // sugiere LÍNEAS de facturación de la propuesta; una vez ganada, sugiere hitos.
  const isWon = data.stage === 'GANADA' || data.stage === 'EN_INVERSION'
  // M5 — la pestaña Ejecución solo aparece en proyectos ganados (o con fase ya asignada).
  const showExecution =
    data.stage === 'GANADA' || data.stage === 'EN_INVERSION' || data.executionPhase != null

  // Usa variables CSS del tema (--bg-input, --border) para respetar modo
  // claro Y oscuro. Sin bg-slate-XXX hardcoded.
  const inputCls =
    'w-full px-3 py-2 rounded-lg border t-primary placeholder:t-faint focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60 disabled:opacity-60'
  const inputStyle: React.CSSProperties = {
    background: 'var(--bg-input)',
    borderColor: 'var(--border)',
  }

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link
            to="/opportunities"
            className="text-xs t-faint hover:underline inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} /> {t('detail.breadcrumb')}
          </Link>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-mono text-sm text-jr2-700 dark:text-jr2-300">{data.code}</span>
            <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
              <Target size={20} className="text-jr2-700 dark:text-jr2-300" />
              {data.title}
            </h2>
          </div>
          <div className="t-muted text-sm mt-1 flex items-center gap-2">
            <Building2 size={14} />
            <Link to="/clients" className="hover:underline">
              {data.clientDisplayName}
            </Link>
            <span className="t-faint">·</span>
            <span>{nameOf(data.serviceLine)}</span>
            <span className="t-faint">·</span>
            <span>{t('enums:PipelineStage.' + data.stage)}</span>
            <BillingModeBadge mode={data.billingMode} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(data.stage === 'GANADA' || data.stage === 'EN_INVERSION') &&
            data.billingMode === 'FACTURABLE' && (
            <button
              type="button"
              onClick={() => setPlanWizardOpen(true)}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
            >
              <CalendarClock size={14} /> {t('billingPlan.button')}
            </button>
          )}
          {data.canWrite && (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="px-3 py-2 rounded-lg text-sm bg-jr2-500/10 text-jr2-700 dark:text-jr2-200 hover:bg-jr2-500/15 flex items-center gap-1.5"
              title={clientEmail ?? t('detail.actions.sendEmailTitle')}
            >
              <Send size={14} /> {t('detail.actions.sendEmail')}
            </button>
          )}
          {edited && data.canWrite && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving || drafts.length === 0 || (Boolean(advisorId) && overCap)}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t('detail.actions.save')}
            </button>
          )}
          <button
            type="button"
            onClick={onDuplicate}
            disabled={duplicating}
            className="px-3 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50 flex items-center gap-1.5"
            title={t('detail.actions.duplicateTitle')}
          >
            {duplicating ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
            {t('detail.actions.duplicate')}
          </button>
          {data.canManage && (
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-2 rounded-lg text-sm bg-rose-500/10 text-rose-700 dark:text-rose-300 hover:bg-rose-500/15 flex items-center gap-1.5"
              title={t('detail.actions.deleteTitle')}
            >
              <Trash2 size={14} /> {t('detail.actions.delete')}
            </button>
          )}
        </div>
      </header>

      {/* M5 — Pestañas (mismo patrón que la ficha de cliente) */}
      <div className="border-b border-base flex items-center gap-1 overflow-x-auto">
        {CASE_TABS.filter((tab) => tab.id !== 'ejecucion' || showExecution).map((tab) => {
          const Icon = tab.icon
          const active = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                'px-4 py-2.5 text-sm flex items-center gap-1.5 border-b-2 transition whitespace-nowrap',
                active
                  ? 'border-jr2-400 text-jr2-700 dark:text-jr2-200'
                  : 'border-transparent t-muted hover:t-primary hover:border-base',
              ].join(' ')}
            >
              <Icon size={14} /> {t(tab.labelKey)}
            </button>
          )
        })}
      </div>

      {activeTab === 'resumen' && (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-4">
          {/* Datos editables */}
          <div className="surface p-5 space-y-3">
            <h3 className="t-primary font-semibold mb-1">{t('detail.form.sectionTitle')}</h3>
            <div>
              <label className="block text-xs t-muted mb-1">{t('detail.form.title')}</label>
              <input
                disabled={disabled}
                value={editForm.title ?? ''}
                onChange={(e) => upd('title', e.target.value)}
                className={inputCls} style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1">{t('detail.form.client')}</label>
              <select
                disabled={disabled}
                value={editForm.clientId ?? data.clientId}
                onChange={(e) => upd('clientId', e.target.value)}
                className={inputCls} style={inputStyle}
              >
                {/* El cliente actual siempre presente aunque no venga en las opciones. */}
                {!clientOptions.some((c) => c.id === data.clientId) && (
                  <option value={data.clientId}>{data.clientDisplayName}</option>
                )}
                {clientOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs t-muted mb-1">{t('detail.form.stage')}</label>
              <select
                disabled={disabled}
                value={editForm.stage ?? data.stage}
                onChange={(e) => upd('stage', e.target.value as PipelineStage)}
                className={inputCls} style={inputStyle}
              >
                {Object.keys(PipelineStageLabel).map((k) => (
                  <option key={k} value={k}>
                    {t('enums:PipelineStage.' + k)}
                  </option>
                ))}
              </select>
            </div>
            {/* LOTE B — líneas de servicio con importe, facturación y recurrencia
                por línea. El backend deriva serviceLine, valor, billingMode y
                sumas facturable/no facturable del detalle. */}
            <fieldset
              className="rounded-lg border p-3 space-y-2"
              style={{ borderColor: 'var(--border)' }}
            >
              <legend className="px-1 text-xs t-muted">{t('detail.form.serviceLinesLegend')}</legend>
              {/* IA: sugerir líneas de facturación desde la propuesta (solo mientras
                  es oportunidad; en proyecto ganado se sugieren hitos en Facturación). */}
              {!disabled && !isWon && (
                <div className="flex justify-end -mt-1">
                  <button
                    type="button"
                    onClick={() => setShowSuggestLines(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/10 hover:bg-jr2-500/20 border border-jr2-500/30"
                  >
                    <Sparkles size={13} /> {t('detail.form.suggestLinesAi')}
                  </button>
                </div>
              )}
              <ServiceLineEditor
                drafts={drafts}
                onChange={(next) => {
                  setDrafts(next)
                  setEdited(true)
                }}
                catalogLines={catalogLines.map((l) => ({ code: l.code, name: l.name }))}
                nameOf={nameOf}
                disabled={disabled}
              />
              {drafts.length === 0 && (
                <p className="text-[11px] text-rose-700 dark:text-rose-300">
                  {t('detail.form.pickAtLeastOneLine')}
                </p>
              )}
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs t-muted mb-1">{t('detail.form.advisor')}</label>
                <select
                  disabled={disabled}
                  value={advisorId}
                  onChange={(e) => onPickAdvisor(e.target.value)}
                  className={inputCls} style={inputStyle}
                >
                  <option value="">{t('detail.form.advisorNone')}</option>
                  {/* Si el asesor asignado está inactivo y no viene en la lista activa, lo añadimos. */}
                  {data.advisorId &&
                    !advisors.some((a) => a.id === data.advisorId) &&
                    data.advisorName && (
                      <option value={data.advisorId}>{t('detail.form.advisorInactive', { name: data.advisorName })}</option>
                    )}
                  {advisors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              {advisorId && (
                <div>
                  <label className="block text-xs t-muted mb-1">{t('detail.form.advisorCommission')}</label>
                  <input
                    disabled={disabled}
                    value={advisorCommissionPct}
                    onChange={(e) => {
                      setAdvisorCommissionPct(e.target.value)
                      setEdited(true)
                    }}
                    placeholder="5"
                    inputMode="decimal"
                    className={`${inputCls} font-mono ${overCap ? '!border-rose-500/60' : ''}`}
                  />
                  {overCap && <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-1">{t('detail.form.maxCap')}</p>}
                  <p className="text-[10px] t-faint mt-1">
                    {advisors.find((a) => a.id === advisorId)?.agreementType === 'EQUITY'
                      ? t('detail.form.advisorEquityHint')
                      : t('detail.form.advisorCommissionHint')}
                  </p>
                </div>
              )}
            </div>
            {(editForm.stage ?? data.stage) === 'PERDIDA' && (
              <div>
                <label className="block text-xs t-muted mb-1">{t('detail.form.lostReason')}</label>
                <textarea
                  disabled={disabled}
                  rows={2}
                  value={editForm.lostReason ?? ''}
                  onChange={(e) => upd('lostReason', e.target.value)}
                  className={inputCls} style={inputStyle}
                />
                {/* La casilla de «cierre administrativo» que vivía aquí era la
                    etapa CANCELADA disfrazada: sacaba del pipeline sin contar
                    como pérdida, pero escondida en este formulario nadie la
                    usaba (1 de 4 pérdidas la tenía marcada). Ahora eso es una
                    columna del Kanban y se elige en el desplegable de etapa.
                    El campo sigue en la base de datos por el histórico. */}
                <p className="text-xs t-muted mt-2">{t('detail.form.notLostHint')}</p>
              </div>
            )}
            <div>
              <label className="block text-xs t-muted mb-1">{t('detail.form.description')}</label>
              <textarea
                disabled={disabled}
                rows={4}
                value={editForm.description ?? ''}
                onChange={(e) => upd('description', e.target.value)}
                className={inputCls} style={inputStyle}
              />
            </div>
            {!data.canWrite && (
              <p className="text-xs t-faint">
                {t('detail.form.readOnlyNotice')}
              </p>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <CaseAiPanel opportunityId={data.id} opportunityCode={data.code} />
          {data.canWrite && (
            <button
              type="button"
              onClick={() => setShowProposalModal(true)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center justify-center gap-1.5"
            >
              <FileSignature size={14} /> {t('detail.actions.generateProposalAi')}
            </button>
          )}
          <AssignmentsPanel
            opportunityId={data.id}
            canManage={data.canManage}
            assignments={data.assignments}
            onChanged={reload}
          />
          <div className="surface p-5 text-sm space-y-1.5">
            <h3 className="t-primary font-semibold mb-2">{t('detail.details.title')}</h3>
            <Row label={t('detail.details.opened')} value={new Date(data.openedAt).toLocaleDateString(currentLocaleTag())} />
            <Row
              label={t('detail.details.closed')}
              value={data.closedAt ? new Date(data.closedAt).toLocaleDateString(currentLocaleTag()) : '—'}
            />
            <Row label={t('detail.details.createdBy')} value={data.createdByName ?? '—'} />
            <Row label={t('detail.details.documents')} value={String(data.documentsCount)} />
            <Row label={t('detail.details.team')} value={String(data.assignedCount)} />
          </div>

          <ProfitabilityCard data={data} />
        </aside>
      </div>
      )}

      {activeTab === 'ejecucion' && showExecution && (
        <ExecutionPanel
          opportunityId={data.id}
          executionPhase={data.executionPhase}
          canWrite={data.canWrite}
          onPhaseChanged={reload}
        />
      )}

      {activeTab === 'actividad' && (
        <div className="space-y-4">
          <CaseCalendarPanel opportunityId={data.id} canWrite={data.canWrite} />
          <CaseTasksPanel opportunityId={data.id} canWrite={data.canWrite} />
          <CaseTimePanel opportunityId={data.id} opportunityCode={data.code} canWrite={data.canWrite} />
          <CaseExpensesPanel
            opportunityId={data.id}
            executionPhase={data.executionPhase}
            canWrite={data.canWrite}
            onChanged={() => void reload()}
          />
        </div>
      )}

      {activeTab === 'facturacion' && (
        <div className="space-y-4">
          <RenewalAlert opportunityId={data.id} />
          <MilestonesPanel clientId={data.clientId} opportunityId={data.id} canWrite={data.canWrite} isProject={isWon} />
          {/* LOTE B — las igualas de la oportunidad siguen visibles en el plan de
              pagos (antes solo se veían en Administración → Igualas). Igual que
              en la ficha del proyecto: se puede crear desde aquí y se avisa de
              lo pactado que aún no tiene iguala. */}
          <OpportunityRecurringPanel
            opportunityId={data.id}
            clientId={data.clientId}
            canWrite={data.canWrite}
            serviceLines={data.serviceLines}
            isWon={isWon}
            billingMode={data.billingMode}
          />
          <FundsProvisionPanel clientId={data.clientId} opportunityId={data.id} />
        </div>
      )}

      {activeTab === 'documentos' && (
        <DocumentsPanel opportunityId={data.id} canWrite={data.canWrite} onChanged={reload} />
      )}

      {activeTab === 'comunicaciones' && (
        <SentEmailsPanel opportunityId={data.id} refreshKey={sentEmailsRefreshKey} />
      )}

      {activeTab === 'cronologia' && (
        <TimelinePanel opportunityId={data.id} canWrite={data.canWrite} />
      )}

      <ProposalGeneratorModal
        open={showProposalModal}
        onClose={() => setShowProposalModal(false)}
        opportunityId={data.id}
        opportunityCode={data.code}
        opportunityTitle={data.title}
        clientId={data.clientId}
      />

      <EmailComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        defaultTo={clientEmail ?? undefined}
        defaultSubject={`[${data.code}] ${data.title}`}
        clientId={data.clientId}
        opportunityId={data.id}
        onSent={() => setSentEmailsRefreshKey((k) => k + 1)}
      />

      {planWizardOpen && data && (
        <BillingPlanWizard
          opportunityId={data.id}
          opportunityTitle={data.title}
          serviceLines={data.serviceLines}
          onClose={() => setPlanWizardOpen(false)}
          onCreated={() => {
            void reload()
          }}
        />
      )}

      <SuggestServiceLinesModal
        open={showSuggestLines}
        onClose={() => setShowSuggestLines(false)}
        opportunityId={data.id}
        catalogLines={catalogLines.map((l) => ({ code: l.code, name: l.name }))}
        onApply={(newDrafts) => {
          setDrafts((prev) => [...prev, ...newDrafts])
          setEdited(true)
          setActiveTab('resumen')
        }}
      />
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="t-muted">{label}</span>
      <span className="t-primary font-medium">{value}</span>
    </div>
  )
}

/**
 * Tarjeta INTERNA de rentabilidad / Coste de Adquisición (CAP).
 * Estilo ámbar como el resto de cifras internas — NUNCA visible para el cliente.
 */
function ProfitabilityCard({ data }: { data: OpportunityDetail }) {
  const { t } = useTranslation('opportunities')
  const p = data.profitability
  const preventaHours = p.acquisitionHoursMinutes / 60
  const ejecucionHours = p.executionHoursMinutes / 60
  const marginPositive = p.grossMarginPct >= 0
  // Solo el porcentaje (sin el nombre del asesor) para que el importe no salte
  // a la línea siguiente.
  const commissionMeta =
    data.advisorCommissionPct != null ? `${fractionToPctInput(data.advisorCommissionPct)} %` : ''

  return (
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4 text-xs space-y-2">
      <div className="flex items-center justify-between text-amber-700 dark:text-amber-300/80">
        <span className="uppercase tracking-wider text-[10px] font-semibold">
          {t('profitability.header')}
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-700 dark:text-amber-300/70">
          {t('profitability.internalBadge')}
        </span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>{t('profitability.revenue')}</span>
        <span className="font-mono">{formatCents(p.revenueCents)}</span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>
          {t('profitability.presalesCost')}
          <span className="text-amber-700 dark:text-amber-300/50 ml-1">({t('profitability.hours', { h: preventaHours.toFixed(1) })})</span>
        </span>
        <span className="font-mono">− {formatCents(p.acquisitionCostCents)}</span>
      </div>

      {p.acquisitionExpensesCents > 0 && (
        <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
          <span>{t('profitability.acquisitionExpenses')}</span>
          <span className="font-mono">− {formatCents(p.acquisitionExpensesCents)}</span>
        </div>
      )}

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>
          {t('profitability.advisorCommission')}
          {commissionMeta &&<span className="text-amber-700 dark:text-amber-300/50 ml-1">({commissionMeta})</span>}
        </span>
        <span className="font-mono">− {formatCents(p.advisorCommissionCents)}</span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80 border-t border-amber-500/10 pt-2 mt-1">
        <span>{t('profitability.cap')}</span>
        <span className="font-mono">− {formatCents(p.capCents)}</span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>
          {t('profitability.executionCost')}
          <span className="text-amber-700 dark:text-amber-300/50 ml-1">({t('profitability.hours', { h: ejecucionHours.toFixed(1) })})</span>
        </span>
        <span className="font-mono">− {formatCents(p.executionCostCents)}</span>
      </div>

      {p.executionExpensesCents > 0 && (
        <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
          <span>{t('profitability.executionExpenses')}</span>
          <span className="font-mono">− {formatCents(p.executionExpensesCents)}</span>
        </div>
      )}

      <div className="flex items-center justify-between text-amber-800 dark:text-amber-100/90 font-semibold border-t border-amber-500/20 pt-2 mt-1">
        <span>{t('profitability.grossMargin')}</span>
        <span className="flex items-baseline gap-2">
          <span
            className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${
              marginPositive
                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
            }`}
          >
            {formatPct(p.grossMarginPct)}
          </span>
          <span className={`font-mono ${marginPositive ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
            {formatCents(p.grossMarginCents)}
          </span>
        </span>
      </div>

      {(p.budgetedCacHours > 0 || p.budgetedExecHours > 0) && (
        <div className="border-t border-amber-500/10 pt-2 mt-2 space-y-1">
          <div className="uppercase tracking-wider text-[9px] font-semibold text-amber-700 dark:text-amber-300/60">
            {t('profitability.budgetedTitle')}
          </div>
          <div className="flex justify-between text-amber-700 dark:text-amber-200/70">
            <span>
              {t('profitability.budgetedCac')}
              <span className="text-amber-700 dark:text-amber-300/40 ml-1">({t('profitability.hoursInt', { h: p.budgetedCacHours })})</span>
            </span>
            <span className="font-mono">{formatCents(p.budgetedCacCostCents)}</span>
          </div>
          <div className="flex justify-between text-amber-700 dark:text-amber-200/70">
            <span>
              {t('profitability.budgetedExec')}
              <span className="text-amber-700 dark:text-amber-300/40 ml-1">({t('profitability.hoursInt', { h: p.budgetedExecHours })})</span>
            </span>
            <span className="font-mono">{formatCents(p.budgetedExecCostCents)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** Formatea una fracción (0.42) como porcentaje es-ES: "42,0 %". Admite negativos. */
function formatPct(fraction: number): string {
  return `${(fraction * 100).toLocaleString(currentLocaleTag(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`
}
